import { GroupSelectItemInterface, GroupSelectOptionsInterface } from '@components/common';

import { useQueryOptionsKpiLineType } from '@hooks/kpis/useQueryOptionsKpiLineType';
import { useQuery } from '@hooks/reactQuery';
import { useQueryUser } from '@hooks/useQueryUser';

import { getKpiAlertById, getKpiAlertForRanById } from '@api/kpiAlerts';

import { extractQueryParams, isEmpty } from '@utils/common';

import { IThreshold } from '@interfaces/alarms/threshold';
import { TNameTitle } from '@interfaces/common/common';
import { TRequiredSelectOption } from '@interfaces/components/componentTypes';
import {
	IKpiAlertDTO,
	IKpiAlertRecipientUserOutDTO,
	IKpiAlertSiteOutDTO,
	IKpiAlertThresholdOutDTO,
} from '@interfaces/kpi/dto/kpiAlertsDTO';
import { IKpiAlertSchema } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/interfaces';
import {
	IGroupSelectCategoryGroup,
	IGroupSelectOptionsKpi,
} from '@components/common/GroupSelectKpi/GroupSelect.types';

import { ETechnologiesValues, EVendorsValues, TVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';
import { EKpiAlertsStatus } from '@enums/kpi-common';

import {
	KPI_ALERTS_BY_ID,
	OPTIONS_LIMENET_KPI_LINE_TYPE,
	OPTIONS_NOKIA_KPI_LINE_TYPE,
} from '@constants/queryKeys';

export interface IKpiAlertInterfaceResponse {
	kpiAlertRule?: IKpiAlertDTO;
	isLoading: boolean;
	formattedKpiAlertRule?: IKpiAlertSchema | undefined;
	refetchKpiAlertRule: () => void;
}

export interface IKpiAlertInterfaceParams {
	id?: string;
	onError?: () => void;
	vendor?: TVendorsValues;
}

export function useKPIAlert({
	id,
	onError,
	vendor,
}: IKpiAlertInterfaceParams): IKpiAlertInterfaceResponse {
	const { user } = useQueryUser();

	const isOranVendor = vendor === EVendorsValues.oran;

	const getKpiAlertByIdFn = isOranVendor
		? getKpiAlertForRanById
		: getKpiAlertById;

	const availableVendors = user?.organization?.availableVendors
		.map((vendor) => vendor.name) || [];

	const {
		optionsKpi: optionsLimenetKpiLineType,
		isLoadingOptionsKpi: isLoadingOptionsLimenetKpiLineType,
	} = useQueryOptionsKpiLineType({
		isKpiView: true,
		vendor: EVendorsValues.limenet,
		queryKey: OPTIONS_LIMENET_KPI_LINE_TYPE,
		availableVendors,
	});

	const {
		optionsKpi: optionsNokiaKpiLineType,
		isLoadingOptionsKpi: isLoadingOptionsNokiaKpiLineType,
	} = useQueryOptionsKpiLineType({
		isKpiView: true,
		vendor: EVendorsValues.nokia,
		queryKey: OPTIONS_NOKIA_KPI_LINE_TYPE,
		availableVendors,
	});

	const { data, isFetching, isLoading, refetch } = useQuery<IKpiAlertDTO>({
		queryKey: [
			KPI_ALERTS_BY_ID,
			id,
		],
		queryFn: extractQueryParams(getKpiAlertByIdFn),
		onError: () => {
			onError?.();
		},
		refetchOnWindowFocus: false,
		enabled: [!!id],
		refetchOnMount: true,
		staleTime: 0,
	});

	const kpi: IGroupSelectOptionsKpi[] | undefined = !data?.vendor?.name
		? undefined
		: (data.vendor.name === EVendorsValues.limenet ? optionsLimenetKpiLineType : optionsNokiaKpiLineType)
			?.filter((option: IGroupSelectOptionsKpi) => {
				return option.categoryGroups.some((categoryGroup: IGroupSelectCategoryGroup) => {
					return categoryGroup.items?.some((item: GroupSelectItemInterface) => {
						return item?.id && data.kpis?.some((kpi) => kpi.id === item.id);
					});
				});
			})
			.map((option: IGroupSelectOptionsKpi) => {
				return {
					...option,
					categoryGroups: option.categoryGroups
						.map((categoryGroup: IGroupSelectCategoryGroup) => {
							return {
								...categoryGroup,
								items: categoryGroup.items?.map((item: GroupSelectItemInterface) => {
									const isChecked: boolean =
										!!item?.id && data.kpis?.some((kpi) => kpi.id === item.id);

									return {
										id: item?.id || '',
										name: item?.id || '',
										title: item?.title || '',
										label: item?.title || '',
										checked: isChecked,
									};
								}) || [],
							};
						})
						.filter((categoryGroup: IGroupSelectCategoryGroup) =>
							categoryGroup.items && categoryGroup.items.length > 0),
				};
			});

	const formattedKpiAlertRule: IKpiAlertSchema | undefined = data && {
		status: data.isActive ? EKpiAlertsStatus.active : EKpiAlertsStatus.inactive,
		severity: data.severity.name,
		ruleName: data.name,
		ruleDescription: data?.description,
		vendor: {
			name: data.vendor.name,
			title: data.vendor.title,
			value: data.vendor.name,
			label: data.vendor.title,
		} as TRequiredSelectOption<TVendorsValues>,
		kpi: kpi || [],
		aggregationType: EAggregationTypes[data?.aggregationType?.name as keyof typeof EAggregationTypes],
		sitesT_4G_FDD: data.technologies
			?.some((tech: TNameTitle) => tech.name.includes(ETechnologiesValues.T_4G_FDD))
			? data.sites.map((site: IKpiAlertSiteOutDTO): GroupSelectOptionsInterface => {
				return {
					category: {
						name: site.siteId,
						title: site.siteId,
					},
					items: isEmpty(site.cellIds) ? [] : site.cellIds.map(cellId => ({
						id: cellId,
						name: cellId,
						title: cellId,
						checked: true,
					})),
				};
			})
			: undefined,
		sitesT_4G_TDD: data.technologies
			?.some((tech: TNameTitle) => tech.name.includes(ETechnologiesValues.T_4G_TDD))
			? data.sites.map((site: IKpiAlertSiteOutDTO): GroupSelectOptionsInterface => {
				return {
					category: {
						name: site.siteId,
						title: site.siteId,
					},
					items: isEmpty(site.cellIds) ? [] : site.cellIds.map(cellId => ({
						id: cellId,
						name: cellId,
						title: cellId,
						checked: true,
					})),
				};
			})
			: undefined,
		sitesT_5G: data.technologies
			?.some((tech: TNameTitle) => tech.name.includes(ETechnologiesValues.T_5G))
			? data.sites.map((site: IKpiAlertSiteOutDTO): GroupSelectOptionsInterface => {
				return {
					category: {
						name: site.siteId,
						title: site.siteId,
					},
					items: isEmpty(site.cellIds) ? [] : site.cellIds.map(cellId => ({
						id: cellId,
						name: cellId,
						title: cellId,
						checked: true,
					})),
				};
			})
			: undefined,
		thresholdSource: data?.thresholdSource.name,
		thresholds: data?.thresholds.map((threshold: IKpiAlertThresholdOutDTO): IThreshold => ({
			kpiId: threshold.kpi.id,
			kpiName: threshold.kpi.title,
			maxThreshold: threshold.maxThreshold,
			minThreshold: threshold.minThreshold,
		})),
		checkEveryHours: {
			label: data?.checkEveryHours,
			value: data?.checkEveryHours,
		},
		checkEveryMinutes: {
			label: data?.checkEveryMinutes,
			value: data?.checkEveryMinutes,
		},
		evaluationWindowHours: {
			label: data?.evaluateHours,
			value: data?.evaluateHours,
		},
		evaluationWindowMinutes: {
			label: data?.evaluateMinutes,
			value: data?.evaluateMinutes,
		},
		isEmail: data?.isEmail,
		messageBody: data?.messageBody,
		messageTitle: data?.messageTitle,
		numberOfRepetitions: data?.valuesOutsideThreshold,
		users: data?.users.map((user: IKpiAlertRecipientUserOutDTO) => {
			return {
				...user,
				label: user.fullName,
				value: user.id,
			};
		}) || [],
		applyFor: data?.thresholdApplication.name,
		notify: data?.notifyCondition.name,
		allowToRenotify: data?.renotifyCondition.name,
		allowToRenotifyAfterCustomValueMinutes: {
			label: data?.renotifyMinutes,
			value: data?.renotifyMinutes,
		},
		allowToRenotifyAfterCustomValueHours: {
			label: data?.renotifyHours,
			value: data?.renotifyHours,
		},
		roles: data?.roles?.map((role: TNameTitle) => {
			return {
				...role,
				label: role.title,
				value: role.name,
			};
		}) || [],
		isNotification: data?.isNotification,
	};

	return {
		kpiAlertRule: data,
		isLoading: isLoading
			|| isFetching
			|| isLoadingOptionsLimenetKpiLineType
			|| isLoadingOptionsNokiaKpiLineType,
		formattedKpiAlertRule,
		refetchKpiAlertRule: refetch,
	};
}
